import { ActivatedRoute, Router } from '@angular/router';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { HotToastService } from '@ngneat/hot-toast';

import { BrandEnum, IdDocTypeEnum, TestPurposeEnum, TestTypeEnum } from 'src/app/core/dtos/enum';
import { TestSessionForRegistrationDto, PersonalLookupInfoForTestRegistrationDto, TestPurposeDto, InstitutionDto, UserAccountIdentityInfoDto, SaveUserRegIdentityInfoRequest } from 'src/app/core/dtos/dto';
import { ProofOfIdentityPageSaveInfo } from 'src/app/core/dtos/reg-data';
import { Step } from 'src/app/core/dtos/step';
import { TestPurposeOption } from 'src/app/core/dtos/test-purpose-option';
import { ConfigHelper } from 'src/app/core/services/helpers/config.helper';
import { RegistrationService } from 'src/app/core/services/registration.service';
import { StepProgressService, PageStepEnum } from 'src/app/core/services/step-progress.service';
import { setRegTestSession, setProofOfIdentity, setTestPurposeOption } from 'src/app/state/reg-data.actions';
import { selectRegTestSession, selectTestPurposeLookup, selectInstitutionLookup, selectTestPurposeOption, selectRegData, selectCollegeLookup } from 'src/app/state/reg-data.selectors';
import { selectAuth } from 'src/app/state/auth.selectors';
import { HttpErrorResponse } from '@angular/common/http';
import { HttpErrorResponseMessages } from 'src/app/core/dtos/http-error-response-messages';
import { DateTimeHelper } from 'src/app/core/services/helpers/date-time.helper';
import { ModalComponent } from 'src/app/shared/components/modal/modal.component';
import { loadShoppingCart } from 'src/app/state/shopping-cart.actions';
import { ShoppingCartService } from 'src/app/core/services/shopping-cart.service';



@Component({
  selector: 'app-proof-of-identity',
  templateUrl: './proof-of-identity.component.html',
  styleUrls: ['./proof-of-identity.component.scss']
})
export class ProofOfIdentityComponent implements OnInit, OnDestroy {

  @ViewChild('proofOfIdentityFormDiv') proofOfIdentityFormDiv: ElementRef;
  @ViewChild('warningSelectionModal') warningSelectionModal: ModalComponent;
  @ViewChild('notificationModal') notificationModal: ModalComponent;

  auth$: Observable<AuthModel>;
  testSessionInformation$: Observable<TestSessionForRegistrationDto>;
  testPurposes$: Observable<TestPurposeDto[]>;
  institutions$: Observable<InstitutionDto[]>;
  subscribedInstitutions$: Observable<Map<TestPurposeEnum, InstitutionDto[]>>;
  selectedTestPurposeOption$: Observable<TestPurposeOption>;
  testSessionInformation: TestSessionForRegistrationDto;
  personalLookupInfo: PersonalLookupInfoForTestRegistrationDto;

  testSessionSubscription: Subscription;
  selectedTestPurposeOptionSubscription: Subscription;
  regDataSubscription: Subscription;
  postRegPersonalInfoSubscription: Subscription;
  getIdentityInfoSubscription: Subscription;
  authSubscription: Subscription;
  validateTestSessionSubscription: Subscription;
  removeFromCartSubscription: Subscription;
  getTestSessionSubscription: Subscription;
  isGeneralTest: boolean;
  steps: Step[];
  continuePressed = false;
  wordPressUrl: string;
  selectedtestPurposeOption: TestPurposeOption;
  brandId: BrandEnum;
  brandName: string;
  identityInfo: UserAccountIdentityInfoDto;
  proofOfIdentityPageSaveInfo: ProofOfIdentityPageSaveInfo;
  existingRegShoppingCartItemId: number;

  warningModalTitle: string;
  warningModalBody: string;
  isWebDelivery: boolean;
  selectedChioce: number;
  notificationModalTitle: string;
  notificationModalBody: string;
  invalidTestSession: boolean;

  constructor(
    private router: Router,
    private stepProgressServiceService: StepProgressService,
    private toastService: HotToastService,
    private registrationService: RegistrationService,
    private configHelper: ConfigHelper,
    private route: ActivatedRoute,
    private store: Store,
    private dateTimeHelper: DateTimeHelper,
    private shoppingCartService: ShoppingCartService,
  ) {
  }

  ngOnInit() {
    this.wordPressUrl = this.configHelper.getWordPressUrl();
    this.brandId = this.configHelper.getBrandId();
    this.brandName = this.configHelper.getBrandName();
    this.auth$ = this.store.pipe(select(selectAuth));
    this.testSessionInformation$ = this.store.pipe(select(selectRegTestSession));
    this.testPurposes$ = this.store.pipe(select(selectTestPurposeLookup));
    this.institutions$ = this.store.pipe(select(selectInstitutionLookup));
    this.subscribedInstitutions$ = this.store.pipe(select(selectCollegeLookup));
    this.selectedTestPurposeOption$ = this.store.pipe(select(selectTestPurposeOption));
    this.selectedChioce = 1;

    this.testSessionSubscription = this.testSessionInformation$
      .subscribe(testSession => {
        this.steps = this.stepProgressServiceService.getSteps(PageStepEnum.ProofOfIdentity);
        this.testSessionInformation = testSession;
        if (testSession?.testTypeId === TestTypeEnum.CELPIP_G) {
          this.isGeneralTest = true;
        } else {
          this.isGeneralTest = false;
        }
        this.isWebDelivery = testSession?.isWebDelivery === true;
      });

    this.selectedTestPurposeOptionSubscription = this.selectedTestPurposeOption$.subscribe(testPurposeOption => {
      if (testPurposeOption) {
        const { testPurposeId } = testPurposeOption;
        this.registrationService.getRegPersonalLookupInfo(this.testSessionInformation.testCentreCountryId, testPurposeId, this.testSessionInformation.testTypeId, this.testSessionInformation.testCentreId)
          .subscribe(
            value => {
              this.personalLookupInfo = value.content;
              if (this.configHelper.getBrandId() === BrandEnum.CELPIP && testPurposeId === TestPurposeEnum.CICImmigrationAndPR || testPurposeId === TestPurposeEnum.CICCitizenship) {
                this.personalLookupInfo.idDocTypes = this.personalLookupInfo.idDocTypes.filter(x => x.id !== IdDocTypeEnum.CertificateOfIndianStatus);
              }
              this.continuePressed = true;
            },
            () => this.toastService.warning("Unable to retrieve Proof of Identity Registration options."));
      }
    });
    this.getTestSession();
  }

  public get BrandEnum() {
    return BrandEnum;
  }

  public get IdDocTypeEnum() {
    return IdDocTypeEnum;
  }

  getIdentityInfo() {
    this.authSubscription = this.auth$
      .subscribe(
        auth => {
          if (auth) {
            this.getIdentityInfoSubscription = this.registrationService.getIdentityInfo()
              .subscribe(
                (result) => {
                  this.identityInfo = result.content;
                });
          }
        }
      );
  }

  ngOnDestroy() {
    this.testSessionSubscription?.unsubscribe();
    this.selectedTestPurposeOptionSubscription?.unsubscribe();
    this.regDataSubscription?.unsubscribe();
    this.postRegPersonalInfoSubscription?.unsubscribe();
    this.validateTestSessionSubscription?.unsubscribe();
    this.removeFromCartSubscription?.unsubscribe();
    this.authSubscription?.unsubscribe();
    this.getIdentityInfoSubscription?.unsubscribe();
    this.getTestSessionSubscription?.unsubscribe();
  }

  getTestSession() {
    this.route.queryParams
      .subscribe(params => {
        const siteCode = params.siteCode ?? this.testSessionInformation?.siteCode;
        const testTypeId = params.testTypeId ?? this.testSessionInformation?.testTypeId;
        if (siteCode && testTypeId) {
          this.getTestSessionSubscription = this.registrationService.getTestSessionInfo(siteCode, testTypeId)
            .subscribe(
              result => {
                if (result?.content?.testCentreName) {
                  this.store.dispatch(setRegTestSession({ testSession: result.content }));
                  this.setTestPurposeLookUp(testTypeId, this.isWebDelivery);
                  //only for TT redirected from WordPress
                  if (params.siteCode && params.testTypeId) {
                    this.validateWebDeliveryTestSession(result.content);
                    this.displayNotificationModal(result.content.isWebDelivery);
                  }
                }
              },
              () => {
                this.store.dispatch(setRegTestSession({ testSession: null }));
                this.toastService.warning('No Test Session selected. Please return to the previous page', { duration: 5000 });
              });
        }
        else {
          this.toastService.warning('No Test Session selected. Please return to the previous page', { duration: 5000 });
        }
      });
  }

  validateWebDeliveryTestSession(testSession: TestSessionForRegistrationDto) {
    if (testSession.isWebDelivery) {
      this.registrationService.ValidateRegionalAccess(this.brandId, testSession.id)
        .subscribe(
          (result) => {
            if (result.content === "NotAvailable") {
              this.toastService.warning(`${this.brandName} Online is not currently available in your region`);
              this.invalidTestSession = true;
            }
            else if (result.content === "RegionalAccessDenied") {
              this.toastService.warning("The test sitting you have selected is not available in your region. Please be sure to select a test sitting within your own country. Click <a href='https://www.cael.ca/search-test-results/?testType=CAEL2'>here</a> to find more test sittings.");
              this.invalidTestSession = true;
            }
          }
        );
    }
  }

  setTestPurposeLookUp(testTypeId: number, isWebDelivery: boolean) {
    this.registrationService.setTestPurposeLookUp(testTypeId, isWebDelivery);
  }

  redirectNextStep() {
    this.router.navigate(['registration/biometric-consent']);
  }

  onContinue(testPurposeOption: TestPurposeOption) {
    this.continuePressed = true;
    this.store.dispatch(setTestPurposeOption({ testPurposeOption }));
    setTimeout(() => {
      this.proofOfIdentityFormDiv.nativeElement.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
    }, 100);
    this.getIdentityInfo();
  }

  onSelectNewTestPurpose(testPurposeOption: TestPurposeOption) {
    this.selectedtestPurposeOption = testPurposeOption;
    this.continuePressed = false;
  }

  onFormSubmit(proofOfIdentityPageSaveInfo: ProofOfIdentityPageSaveInfo) {
    this.proofOfIdentityPageSaveInfo = proofOfIdentityPageSaveInfo;
    this.validateTestSessionSubscription = this.registrationService.validateTestSession(this.testSessionInformation.testTypeId, this.testSessionInformation.id)
      .subscribe(
        result => {
          if (result.content.existingRegTestSessionId !== null && result.content.existingRegTestSessionId !== this.testSessionInformation.id) {
            let locationandDate = "";
            this.existingRegShoppingCartItemId = result.content.existingRegShoppingCartItemId;
            if (result.content.webDeliveryReg) {
              locationandDate = "Online Test Date: " + this.dateTimeHelper.formatBrowserDate(result.content.testDateTimeUtc) + "<br/>"
                + "(by time zone: " + this.dateTimeHelper.getLocalTimeZone() + ")<br/><br/>";
            }
            else {
              locationandDate = "Location: " + result.content.existingRegTestLocation + "<br/>"
                + "Date: " + result.content.existingRegTestDateTime + "<br/><br/>";
            }
            this.warningModalTitle = "Select Test";
            this.warningModalBody = "Please note that you already have a test registration in your shopping cart for:<br/><br/>"
              + locationandDate
              + "Selecting a different test will remove this existing test from your shopping cart.<br/><br/>Would you like to continue?";
            this.warningSelectionModal.open();
          }
          else {
            this.saveProofOfIdentity();
          }
        },
        (error: HttpErrorResponse) => {
          if (error.status !== 401) {
            const errorMessage = error.error as HttpErrorResponseMessages;
            errorMessage.reasons.forEach(reasons => {
              this.toastService.warning(reasons.errorMessage);
            });
          }
        });
  }

  removeExistingSitting() {
    this.removeFromCartSubscription = this.shoppingCartService.removeShoppingCartItem(this.existingRegShoppingCartItemId)
      .subscribe(
        result => {
          this.store.dispatch(loadShoppingCart({ shoppingCart: result.content }));
          this.saveProofOfIdentity();
        },
        () => {
          this.toastService.warning("Unable to remove item from shopping cart");
        }
      );
  }

  saveProofOfIdentity() {
    this.store.dispatch(setProofOfIdentity({ proofOfIdentity: this.proofOfIdentityPageSaveInfo }));
    const postRegPersonalRequest: SaveUserRegIdentityInfoRequest = {
      additionalComments: "",
      admissionInstitutionId: this.selectedtestPurposeOption.institutionId,
      birthDate: this.proofOfIdentityPageSaveInfo.dateOfBirth,
      brandId: this.brandId,
      citizenshipCountryId: this.proofOfIdentityPageSaveInfo.countryId,
      firstLanguageId: this.proofOfIdentityPageSaveInfo.firstLanguageId,
      firstName: this.proofOfIdentityPageSaveInfo.firstName,
      firstNameLocalLanguage: this.proofOfIdentityPageSaveInfo.firstName,
      gender: this.proofOfIdentityPageSaveInfo.gender,
      idDocExpiryDate: this.proofOfIdentityPageSaveInfo.documentExpiryDate,
      idDocExpiryDate2: null,
      idDocNumber: this.proofOfIdentityPageSaveInfo.documentNumber,
      idDocNumber2: null,
      idDocTypeId: this.proofOfIdentityPageSaveInfo.documentTypeId,
      idDocTypeId2: null,
      lastName: this.proofOfIdentityPageSaveInfo.lastName,
      lastNameLocalLanguage: this.proofOfIdentityPageSaveInfo.lastName,
      middleName: this.proofOfIdentityPageSaveInfo.middleName,
      middleNameLocalLanguage: this.proofOfIdentityPageSaveInfo.middleName,
      regTimeZoneUsed: this.dateTimeHelper.getLocalTimeZone(),
      testPurposeId: this.selectedtestPurposeOption.testPurposeId,
      testSessionId: this.testSessionInformation.id,
      testTypeId: this.testSessionInformation.testTypeId,
    };
    this.postRegPersonalInfoSubscription = this.registrationService.postRegPersonalInfo(postRegPersonalRequest).subscribe(
      () => {
        this.redirectNextStep();
      },
      (error: HttpErrorResponse) => {
        if (error.status !== 401) {
          const errorMessage = error.error as HttpErrorResponseMessages;
          if (errorMessage?.reasons) {
            errorMessage.reasons.forEach(reasons => {
              this.toastService.warning(reasons.errorMessage);
            });
          }
          else {
            this.toastService.warning("Failed to load identity information because of unknown problem. Please contact Paragon for support.");
          }
        }
      }
    );
  }

  redirectWordPress() {
    window.location.href = this.wordPressUrl;
  }

  displayNotificationModal(isWebDelivery: boolean) {
    this.notificationModalTitle = "Important Note";
    const identityDocumentCheck = this.brandId === BrandEnum.CAEL ? 'https://www.cael.ca/take-cael/accepted-identification/' : "https://www.celpip.ca/take-celpip/test-day-information/";
    if (isWebDelivery) {
      this.notificationModalBody = `Before beginning your ${this.brandName} online registration, ensure that you have your <a href = ${identityDocumentCheck} target="_blank"> acceptable identity document </a>
      and an accepted form of payment on hand, as they are required to complete the registration. Please also be aware that you will be required to take a photo of yourself during the registration.`;
    }
    else {
      this.notificationModalBody = `Before beginning your ${this.brandName} registration, ensure that you have your <a href = ${identityDocumentCheck} target="_blank"> acceptable identity document </a>
      and an accepted form of payment on hand, as they are required to complete the registration.`;
    }
    this.notificationModal.open();
  }

  displayLoginForm() {
    this.selectedChioce = 1;
  }

}
